import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Sparkles, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STORAGE_KEY = "ne_beta_banner_dismissed_v1";

export default function BetaBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") setOpen(false);
    } catch {}
  }, []);

  // Eksponer banner-høyden som CSS-variabel slik at Header kan sticky-stack under den
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--beta-banner-h", open ? "44px" : "0px");
    return () => { root.style.setProperty("--beta-banner-h", "0px"); };
  }, [open]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
  };

  const send = async () => {
    if (message.trim().length < 5) {
      toast.error("Skriv minst noen ord før du sender.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim(),
        page_url: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
        contact: contact.trim() || undefined,
      });
      toast.success("Takk! Vi har mottatt tilbakemeldingen din.");
      setShowForm(false);
      setMessage("");
      setContact("");
      setKind("bug");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kunne ikke sende. Prøv igjen senere.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed top-0 left-0 right-0 z-[45] border-b border-[var(--magenta)]/40 text-[var(--cream)]"
          style={{ height: "44px", background: "#1A0E14" }}
          data-testid="beta-banner"
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-4 h-full flex items-center gap-2 sm:gap-3 text-sm">
            <Sparkles className="h-3.5 w-3.5 text-[var(--magenta)] shrink-0" />
            {/* Mobile: kort versjon. Desktop: full tekst */}
            <div className="flex-1 leading-tight min-w-0 truncate sm:whitespace-normal sm:overflow-visible">
              <span className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mr-2">{t("forum.beta_label").toUpperCase()}</span>
              <span className="font-fraunces italic hidden sm:inline">{t("forum.beta_text")}</span>{" "}
              <span className="text-[var(--cream-soft)] hidden sm:inline">{t("forum.beta_full")}</span>
              <span className="text-[var(--cream-soft)] sm:hidden italic font-fraunces">{t("forum.beta_text")}</span>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] hover:text-[var(--cream)] underline-offset-4 hover:underline shrink-0 hidden sm:inline-block"
              data-testid="beta-banner-feedback"
            >
              {t("forum.beta_send_feedback").toUpperCase()}
            </button>
            <button
              onClick={dismiss}
              className="text-[var(--cream-soft)] hover:text-[var(--cream)] shrink-0 p-1 -mr-1"
              aria-label={t("cookies.close")}
              data-testid="beta-banner-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* TILBAKEMELDING-FAB fjernet i Iter 49 (var overflødig - finnes tilsvarende
          knapp øverst i banneret). Modal under er fortsatt aktiv og åpnes via
          knappen i banneret (linje ~90, "SEND TILBAKEMELDING"). */}

      {/* Modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
          data-testid="feedback-modal"
        >
          <div className="bg-[var(--noir)] border border-white/15 max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowForm(false)}
              className="absolute top-3 right-3 text-[var(--cream-soft)] hover:text-[var(--cream)]"
              aria-label="Lukk"
              data-testid="feedback-modal-close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="font-meta text-[10px] tracking-[0.25em] text-[var(--magenta)] mb-2">TILBAKEMELDING</div>
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">Si fra hva du tenker</h2>
            <p className="text-sm text-[var(--cream-soft)] mb-5">Alt fra rare feil til ønsker du har for plattformen. Vi leser alt.</p>

            <div className="mb-3">
              <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1.5">TYPE</div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: "bug", l: "Noe virker ikke" },
                  { v: "wish", l: "Forslag" },
                  { v: "praise", l: "Ros" },
                  { v: "other", l: "Annet" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setKind(opt.v)}
                    className={`px-3 py-1.5 text-xs border ${kind === opt.v ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)] hover:border-white/30"}`}
                    data-testid={`feedback-kind-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="Beskriv kort hva som skjedde eller hva du ønsker deg…"
              className="input-editorial w-full resize-none text-sm"
              data-testid="feedback-message"
            />
            <div className="font-meta text-[9px] text-[var(--cream-faint)] mt-1 text-right">{message.length}/2000</div>

            <div className="mt-3">
              <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1.5">KONTAKT (VALGFRITT)</div>
              <input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                maxLength={200}
                placeholder="E-post eller brukernavn så vi kan svare deg"
                className="input-editorial w-full text-sm"
                data-testid="feedback-contact"
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-xs font-meta tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--cream)]"
                data-testid="feedback-cancel"
              >
                AVBRYT
              </button>
              <button
                onClick={send}
                disabled={submitting || message.trim().length < 5}
                className="px-5 py-2 text-xs font-meta tracking-[0.2em] bg-[var(--magenta)] text-[var(--noir)] hover:bg-[var(--magenta-soft)] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="feedback-send"
              >
                {submitting ? "SENDER…" : "SEND"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
